import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  Card, 
  CardContent, 
  Chip,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TimelineIcon from '@mui/icons-material/Timeline';
import CompareIcon from '@mui/icons-material/Compare';
import EventIcon from '@mui/icons-material/Event';
import SensorsIcon from '@mui/icons-material/Sensors';
import SensorTimeSeries from './SensorTimeSeries';
import SimpleSensorChart from './SimpleSensorChart';
import useSensorData from '../hooks/useSensorData';
import { formatDistanceToNow } from 'date-fns';

const Dashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { loading, data, error, selectedTunnel, changeTunnel } = useSensorData();
  const [sensorStats, setSensorStats] = useState({
    sensor1: { current: 0, change: 0, belowThreshold: false },
    sensor2: { current: 0, change: 0, belowThreshold: false }
  });
  const [nextWash, setNextWash] = useState(null);

  // Calculate statistics when data is loaded
  useEffect(() => {
    if (data && data.sensorData && data.sensorData.length > 0) {
      const threshold = data.threshold || 66;
      const lastEntry = data.sensorData[data.sensorData.length - 1];
      const entryOneWeekAgo = data.sensorData.find(entry => 
        new Date(entry.timestamp) < new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
      ) || data.sensorData[0];
      
      setSensorStats({
        sensor1: {
          current: lastEntry.m1 !== null ? Math.round(lastEntry.m1) : 'N/A',
          change: lastEntry.m1 !== null && entryOneWeekAgo.m1 !== null 
            ? Math.round(lastEntry.m1 - entryOneWeekAgo.m1) 
            : 0,
          belowThreshold: lastEntry.m1 !== null ? lastEntry.m1 < threshold : false
        },
        sensor2: {
          current: lastEntry.m2 !== null ? Math.round(lastEntry.m2) : 'N/A',
          change: lastEntry.m2 !== null && entryOneWeekAgo.m2 !== null 
            ? Math.round(lastEntry.m2 - entryOneWeekAgo.m2) 
            : 0,
          belowThreshold: lastEntry.m2 !== null ? lastEntry.m2 < threshold : false
        }
      });

      // Set next wash date (this would come from the prediction model)
      // For demo purposes, we'll set it to 2 weeks from now
      setNextWash(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000));
    }
  }, [data]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '500px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">Error loading sensor data</Typography>
        <Typography>{error.message}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          Tunnel Sensor Dashboard
        </Typography>
        
        {data?.tunnels && (
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel id="tunnel-select-label">Tunnel</InputLabel>
            <Select
              labelId="tunnel-select-label"
              id="tunnel-select"
              value={selectedTunnel || ''}
              label="Tunnel"
              onChange={(e) => changeTunnel(e.target.value || null)}
            >
              <MenuItem value="">All Tunnels</MenuItem>
              {data.tunnels.map(tunnel => (
                <MenuItem key={tunnel} value={tunnel}>{tunnel}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
      
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Sensor 1 Stats */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                M1 Readings {selectedTunnel ? `- Tunnel ${selectedTunnel}` : ''}
              </Typography>
              <Typography variant="h4" component="div">
                {sensorStats.sensor1.current}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Chip 
                  label={sensorStats.sensor1.belowThreshold ? "Below Threshold" : "Above Threshold"} 
                  color={sensorStats.sensor1.belowThreshold ? "error" : "success"}
                  size="small"
                  sx={{ mr: 1 }}
                />
                <Typography 
                  variant="body2"
                  color={sensorStats.sensor1.change >= 0 ? "success.main" : "error.main"}
                >
                  {sensorStats.sensor1.change >= 0 ? "+" : ""}{sensorStats.sensor1.change} (7d)
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Sensor 2 Stats */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                M2 Readings {selectedTunnel ? `- Tunnel ${selectedTunnel}` : ''}
              </Typography>
              <Typography variant="h4" component="div">
                {sensorStats.sensor2.current}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Chip 
                  label={sensorStats.sensor2.belowThreshold ? "Below Threshold" : "Above Threshold"} 
                  color={sensorStats.sensor2.belowThreshold ? "error" : "success"}
                  size="small"
                  sx={{ mr: 1 }}
                />
                <Typography 
                  variant="body2"
                  color={sensorStats.sensor2.change >= 0 ? "success.main" : "error.main"}
                >
                  {sensorStats.sensor2.change >= 0 ? "+" : ""}{sensorStats.sensor2.change} (7d)
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Last Wash */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Last Wash
              </Typography>
              <Typography variant="h6" component="div">
                {data?.washDates?.length > 0 
                  ? new Date(data.washDates[data.washDates.length - 1]).toLocaleDateString() 
                  : 'No data'}
              </Typography>
              <Typography color="textSecondary" variant="body2">
                {data?.washDates?.length > 0 
                  ? formatDistanceToNow(new Date(data.washDates[data.washDates.length - 1]), { addSuffix: true }) 
                  : ''}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Next Wash Prediction */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Predicted Next Wash
              </Typography>
              <Typography variant="h6" component="div">
                {nextWash ? nextWash.toLocaleDateString() : 'Calculating...'}
              </Typography>
              <Typography color="textSecondary" variant="body2">
                {nextWash ? formatDistanceToNow(nextWash, { addSuffix: true }) : ''}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Chart */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Sensor Readings (Last 90 Days)
          </Typography>
          <Box>
            <Button
              size="small"
              startIcon={<TimelineIcon />}
              onClick={() => {
                // Navigate to tunnel page using selected tunnel or default to all sensors
                if (selectedTunnel) {
                  navigate(`/tunnels/${selectedTunnel}`);
                } else {
                  navigate('/sensors');
                }
              }}
              sx={{ mr: 1 }}
            >
              Detailed View
            </Button>
            <Button
              size="small"
              startIcon={<SensorsIcon />}
              onClick={() => navigate('/sensors')}
            >
              All Sensors
            </Button>
          </Box>
        </Box>
        <Box sx={{ height: 300, position: 'relative' }}>
          {/* Debug panel */}
          <Box sx={{ 
            position: 'absolute', 
            top: 10, 
            left: 10, 
            zIndex: 10,
            backgroundColor: 'rgba(255,255,255,0.8)',
            padding: 1,
            borderRadius: 1,
            fontSize: '10px'
          }}>
            <Typography variant="caption" display="block">
              Tunnel: {selectedTunnel || 'All'} | Points: {data?.sensorData?.length || 0}
            </Typography>
            <Typography variant="caption" display="block">
              Has m1: {data?.sensorData?.some(i => i.m1 !== null && i.m1 !== undefined) ? 'Yes' : 'No'} |
              Has m2: {data?.sensorData?.some(i => i.m2 !== null && i.m2 !== undefined) ? 'Yes' : 'No'}
            </Typography>
          </Box>
        
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Loading sensor data...
              </Typography>
            </Box>
          ) : (
            data && data.sensorData && data.sensorData.length > 0 ? (
              <Box sx={{ height: '100%', position: 'relative' }}>
                {data.sensorData.some(item => item.m1 !== null && item.m1 !== undefined) ? (
                  <SimpleSensorChart 
                    data={data.sensorData.slice(-90)} 
                    sensorId="m1"
                    threshold={data.threshold || 66}
                  />
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100%', 
                    border: '1px dashed #ccc',
                    borderRadius: 1
                  }}>
                    <Typography color="error">No valid m1 data available</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Data exists but contains no valid values
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography variant="body2" color="text.secondary">
                  No sensor data available
                </Typography>
              </Box>
            )
          )}
        </Box>
      </Paper>
      
      {/* Quick Actions */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            height: '100%', 
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 3,
            }
          }} onClick={() => {
              // Navigate to tunnel page or all sensors
              if (selectedTunnel) {
                navigate(`/tunnels/${selectedTunnel}`);
              } else {
                navigate('/sensors');
              }
            }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TimelineIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Sensor Details
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                View detailed time series data for each sensor with customizable date ranges and interactive markers.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            height: '100%', 
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 3,
            }
          }} onClick={() => navigate('/comparison')}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CompareIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Sensor Comparison
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Compare readings from multiple sensors side by side with difference analysis and correlation metrics.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            height: '100%', 
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 3,
            }
          }} onClick={() => navigate('/predictions')}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EventIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Wash Predictions
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                View predictions for upcoming wash dates based on sensor data trends and historical patterns.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;